import { useCallback, useRef } from 'react';

export interface CanvasResource {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  cleanup: () => void;
}

export interface ElementFontShift {
  element: HTMLElement;
  fontShift: number;
}

/**
 * Minimum amount of shift (in pixels) required before applying a correction
 */
const TEXT_INK_SHIFT_THRESHOLD = 0.7;

/**
 * Multiplier to adjust the shift amount for a more conservative correction
 */
const TEXT_INK_SHIFT_MULTIPLIER = 0.5;

/**
 * Calculates how much to shift text vertically to make it appear truly centered
 *
 * THE PROBLEM:
 * When we center text with CSS, the browser centers the font's invisible box,
 * not the actual visible characters. Many fonts have more empty space above
 * or below the text, causing them to look off-center even when they're
 * technically centered.
 *
 * ```
 *            What the browser centers:
 *         ┌───────────────────────────┐
 *         │    Font's Invisible Box   │
 *         │                           │
 *         │        ┌─────────┐        │ ← Extra space above text
 *         │        │  Hello  │        │   makes it appear too low
 *         │        └─────────┘        │
 *         │                           │ ← Less space below text
 *         └───────────────────────────┘
 *                    ↓
 *            What we want to center:
 *               ┌─────────┐
 *               │  Hello  │
 *               └─────────┘
 * ```
 *
 * This function measures how much extra space exists above vs. below the actual
 * visible text and calculates the shift needed to make it look centered.
 * A completely centered text will have the same amount of empty space above and below.
 * But we apply a threshold to prevent micro-adjustments for fonts with minimal asymmetry.
 *
 * @param ctx - Canvas context with font already applied
 * @param text - The text to measure
 * @param threshold - Minimum shift needed to apply correction
 * @param multiplier - Multiplier to adjust the shift amount
 * @returns The vertical shift in pixels (positive = shift up, negative = shift down)
 */
const measureFontShift = (ctx: CanvasRenderingContext2D, text: string, threshold: number, multiplier: number) => {
  const textMetrics = ctx.measureText(text);
  // Measure empty space at the top: (fontBox.top - actualText.top)
  const topTextBoundingBoxDifference = textMetrics.fontBoundingBoxAscent - textMetrics.actualBoundingBoxAscent;
  // Measure empty space at the bottom: (fontBox.bottom - actualText.bottom)
  const bottomTextBoundingBoxDifference = textMetrics.fontBoundingBoxDescent - textMetrics.actualBoundingBoxDescent;
  // Calculate the difference between these spaces (a value close to 0 means the text is centered)
  const shift = (topTextBoundingBoxDifference - bottomTextBoundingBoxDifference) * Math.abs(multiplier);
  if (Math.abs(shift) > threshold) {
    /**
     * Y axis grows downward in web browsers:
     *   0 ┌─→ x
     *     │
     *   1 │
     *     │
     *   2 │
     *     ↓ y
     *
     * So we negate the shift to get the correct direction:
     * - Positive shift (moves down) should become negative (moves up)
     * - Negative shift (moves up) should become positive (moves down)
     */
    return -shift;
  }
  return 0;
};

const createCanvas = (): CanvasResource | null => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }
  const cleanup = () => canvas.remove?.();
  return { canvas, ctx, cleanup };
};

/**
 * Calculate the font shift needed to center font glyphs within their bounding box
 *
 * @param element - HTML element to evaluate
 * @param ctx - Canvas context with font already applied
 * @param threshold - Minimum shift needed to apply correction (default: 0.7px)
 * @param multiplier - Multiplier to adjust the shift amount (default: 0.5)
 * @returns Object with element and its font shift amount
 */
export const calculateElementFontShift = (
  element: HTMLElement,
  ctx: CanvasRenderingContext2D,
  threshold = TEXT_INK_SHIFT_THRESHOLD,
  multiplier = TEXT_INK_SHIFT_MULTIPLIER,
): ElementFontShift | undefined => {
  try {
    const text = element.textContent;
    if (!text) {
      // ignore empty text
      return undefined;
    }
    // load the font into the canvas to measure the text
    const style = getComputedStyle(element);
    ctx.font = style.font;
    const fontShift = measureFontShift(ctx, text, threshold, multiplier);
    if (fontShift) {
      return {
        element,
        fontShift,
      };
    }
    // ignore text that doesn't need shifting
    return undefined;
  } catch (e) {
    console.warn('Failed to calculate font shift to center text:', e);
    return undefined;
  }
};

/**
 * Hook that returns a function for correcting font glyph optical centering issues.
 *
 * This hook solves a problem that CSS alignments (flex, grid, etc.) cannot fix:
 * the optical illusion created by font designs where the actual ink/glyphs have
 * uneven vertical distribution within their bounding box. No amount of CSS
 * container centering can fix this, as it's an issue with the font itself.
 *
 * ```
 * CSS centers the container perfectly, but the font's ink is off-center:
 *
 * ┌───────────────────┐            ┌───────────────────┐
 * │ ┌───────────────┐ │            │ ┌───────────────┐ │
 * │ │               │ │            │ │               │ │
 * │ │               │ │ ← CSS      │ │     Text      │ │ ← Hook adjusts
 * │ │     Text      │ │   centers  │ │  (corrected)  │ │   the text
 * │ │  (off-center) │ │   box      │ │               │ │   position
 * │ └───────────────┘ │            │ └───────────────┘ │
 * └───────────────────┘            └───────────────────┘
 *     Container centered             Container centered
 *     Font ink is not                Font ink appears centered
 * ```
 *
 * @returns Function to apply optical font centering to one or more HTML elements
 *
 */
export const useOpticalFontCenter = () => {
  const processedElementsRef = useRef<HTMLElement[]>([]);

  const validateProcessedElements = (elements: HTMLElement[]) => {
    elements.forEach((element) => {
      const processedElement = processedElementsRef.current.find((proccessedElement) => proccessedElement === element);
      if (processedElement && processedElement.style.font !== element.style.font) {
        processedElementsRef.current = processedElementsRef.current.filter((el) => el !== processedElement);
        return;
      }
    });
  };
  return useCallback((...elements: HTMLElement[]) => {
    const createdCanvas = createCanvas();
    if (!createdCanvas) {
      // failed to create canvas
      console.warn('Failed to create canvas for optical font centering');
      return;
    }
    const { ctx, cleanup } = createdCanvas;
    validateProcessedElements(elements);
    elements
      .filter((el) => !processedElementsRef.current.includes(el))
      .forEach((element) => {
        const currentTransform = element.style.transform;
        if (currentTransform?.includes('translateY')) {
          // assume if element already has a translateY transform, we don't need to adjust it
          processedElementsRef.current.push(element);
          return;
        }
        const { fontShift } = calculateElementFontShift(element, ctx) ?? {};
        if (fontShift) {
          // apply the shift to the element preserving existing transforms
          element.style.transform = currentTransform
            ? `${currentTransform} translateY(${fontShift}px)`
            : `translateY(${fontShift}px)`;
        }
        processedElementsRef.current.push(element);
      });
    cleanup();
  }, []);
};
